import { useState, useRef, useCallback } from "react";
import QRCode from "react-qr-code";
import {
  Download, Plus, Trash2, Copy, Check,
  QrCode as QrCodeIcon, X, RefreshCw, Palette, Upload, ImagePlus,
} from "lucide-react";
import { toast } from "react-toastify";

// ── Dibuja un QR + logo/Q en el centro → PNG ─────────────────
function svgQRtoPNG({ svgEl, fgColor, bgColor, outputSize, label, qColor, logoDataUrl }) {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement("canvas");
    canvas.width  = outputSize;
    canvas.height = outputSize;
    const ctx = canvas.getContext("2d");

    ctx.fillStyle = bgColor;
    ctx.fillRect(0, 0, outputSize, outputSize);

    const serializer = new XMLSerializer();
    let svgStr = serializer.serializeToString(svgEl);
    if (!svgStr.includes("xmlns=")) {
      svgStr = svgStr.replace("<svg", `<svg xmlns="http://www.w3.org/2000/svg"`);
    }

    const img = new Image();
    img.src = "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(svgStr)));

    img.onload = () => {
      const pad = Math.round(outputSize * 0.06);
      ctx.drawImage(img, pad, pad, outputSize - pad * 2, outputSize - pad * 2);

      const cx = outputSize / 2;
      const cy = outputSize / 2;
      const r  = Math.round(outputSize * 0.095);

      // Círculo de fondo central
      ctx.fillStyle = bgColor;
      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      ctx.fill();

      const finish = () => resolve({ dataUrl: canvas.toDataURL("image/png"), label });

      if (logoDataUrl) {
        const logoImg = new Image();
        logoImg.src = logoDataUrl;
        logoImg.onload = () => {
          const lr = Math.round(r * 0.82);
          ctx.save();
          ctx.beginPath();
          ctx.arc(cx, cy, lr, 0, Math.PI * 2);
          ctx.clip();
          ctx.drawImage(logoImg, cx - lr, cy - lr, lr * 2, lr * 2);
          ctx.restore();
          finish();
        };
        logoImg.onerror = () => {
          // Fallback a Q si la imagen falla
          ctx.fillStyle = qColor;
          ctx.font = `900 ${Math.round(r * 1.35)}px system-ui, -apple-system, Arial, sans-serif`;
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";
          ctx.fillText("Q", cx, cy + Math.round(r * 0.06));
          finish();
        };
      } else {
        ctx.fillStyle = qColor;
        ctx.font = `900 ${Math.round(r * 1.35)}px system-ui, -apple-system, Arial, sans-serif`;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText("Q", cx, cy + Math.round(r * 0.06));
        finish();
      }
    };

    img.onerror = reject;
  });
}

// ── Logo / Q en el centro del preview ────────────────────────
function QCenter({ bgColor, qColor, size = 50, logoDataUrl }) {
  return (
    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
      <div
        className="rounded-full flex items-center justify-center overflow-hidden"
        style={{
          width:     size,
          height:    size,
          background: bgColor,
          boxShadow: `0 0 0 6px ${bgColor}`,
        }}
      >
        {logoDataUrl ? (
          <img
            src={logoDataUrl}
            alt="logo"
            style={{ width: "82%", height: "82%", objectFit: "contain" }}
          />
        ) : (
          <span
            style={{
              fontSize:   Math.round(size * 0.62),
              fontWeight: 900,
              color:      qColor,
              fontFamily: "system-ui, -apple-system, Arial, sans-serif",
              lineHeight: 1,
              userSelect: "none",
            }}
          >
            Q
          </span>
        )}
      </div>
    </div>
  );
}

// ── Tarjeta de QR guardado ────────────────────────────────────
function SavedQRCard({ q, onDelete, onLoad }) {
  const ref = useRef(null);
  const [dl, setDl] = useState(false);

  const handleDownload = async () => {
    const svgEl = ref.current?.querySelector("svg");
    if (!svgEl) return;
    setDl(true);
    try {
      const { dataUrl, label } = await svgQRtoPNG({
        svgEl,
        fgColor:     q.fgColor,
        bgColor:     q.bgColor,
        outputSize:  800,
        label:       q.label,
        qColor:      q.qColor,
        logoDataUrl: q.logoDataUrl ?? null,
      });
      const a = document.createElement("a");
      a.download = `qr-${(label || "qkiere").replace(/\s+/g, "-").toLowerCase()}.png`;
      a.href = dataUrl;
      a.click();
      toast.success("QR descargado");
    } catch { toast.error("Error al descargar"); }
    finally { setDl(false); }
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-3 flex flex-col items-center gap-2">
      <div
        ref={ref}
        className="relative flex items-center justify-center rounded-xl overflow-hidden"
        style={{ background: q.bgColor, padding: 8 }}
      >
        <QRCode value={q.url || " "} size={120} bgColor={q.bgColor} fgColor={q.fgColor} level="H" />
        <QCenter bgColor={q.bgColor} qColor={q.qColor} size={28} logoDataUrl={q.logoDataUrl ?? null} />
      </div>

      <p className="text-xs font-semibold text-gray-700 text-center truncate w-full px-1">
        {q.label || q.url}
      </p>
      <p className="text-[10px] text-gray-400 truncate w-full text-center px-1">{q.url}</p>

      <div className="flex gap-1.5 w-full">
        <button
          onClick={onLoad}
          className="flex-1 flex items-center justify-center gap-1 py-1.5 rounded-lg bg-gray-100 text-gray-600 text-[11px] font-semibold hover:bg-gray-200 transition"
          title="Cargar en el editor"
        >
          <RefreshCw size={11} /> Editar
        </button>
        <button
          onClick={handleDownload}
          disabled={dl}
          className="flex-1 flex items-center justify-center gap-1 py-1.5 rounded-lg bg-supernova-50 text-supernova-700 text-[11px] font-semibold hover:bg-supernova-100 transition disabled:opacity-50"
          title="Descargar PNG"
        >
          <Download size={11} /> PNG
        </button>
        <button
          onClick={onDelete}
          className="p-1.5 rounded-lg bg-red-50 text-red-400 hover:bg-red-100 transition"
          title="Eliminar"
        >
          <Trash2 size={11} />
        </button>
      </div>
    </div>
  );
}

// ── Página principal ──────────────────────────────────────────
const PRESETS = [
  { name: "Clásico", fgColor: "#1a1a2e", bgColor: "#ffffff", qColor: "#f59e0b" },
  { name: "Naranja", fgColor: "#c2410c", bgColor: "#fff7ed", qColor: "#ea580c" },
  { name: "Dark",    fgColor: "#f8fafc", bgColor: "#1e293b", qColor: "#fbbf24" },
  { name: "Verde",   fgColor: "#14532d", bgColor: "#f0fdf4", qColor: "#16a34a" },
  { name: "Azul",    fgColor: "#1e3a8a", bgColor: "#eff6ff", qColor: "#3b82f6" },
];

const SIZE_OPTIONS = [
  { label: "Pequeño", value: 200 },
  { label: "Mediano", value: 300 },
  { label: "Grande",  value: 400 },
];

export default function QRGenerator() {
  const [url,          setUrl]         = useState("https://qkiere.com");
  const [label,        setLabel]       = useState("");
  const [fgColor,      setFgColor]     = useState("#1a1a2e");
  const [bgColor,      setBgColor]     = useState("#ffffff");
  const [qColor,       setQColor]      = useState("#f59e0b");
  const [logoDataUrl,  setLogoDataUrl] = useState(null);
  const [previewSize,  setPreviewSize] = useState(300);
  const [copying,      setCopying]     = useState(false);
  const [saving,       setSaving]      = useState(false);
  const [saved,        setSaved]       = useState(() => {
    try { return JSON.parse(localStorage.getItem("qk_qr_saved") || "[]"); } catch { return []; }
  });

  const qrRef       = useRef(null);
  const logoInputRef = useRef(null);

  // ── Carga logo desde archivo ─────────────────────────────────
  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error("La imagen debe ser menor a 2 MB");
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => setLogoDataUrl(ev.target.result);
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  // ── Descarga PNG del preview actual ─────────────────────────
  const handleDownload = useCallback(async () => {
    const svgEl = qrRef.current?.querySelector("svg");
    if (!svgEl || !url) return;
    try {
      const { dataUrl } = await svgQRtoPNG({
        svgEl, fgColor, bgColor, outputSize: 800, label, qColor, logoDataUrl,
      });
      const a = document.createElement("a");
      a.download = `qr-${(label || "qkiere").replace(/\s+/g, "-").toLowerCase()}.png`;
      a.href = dataUrl;
      a.click();
      toast.success("¡QR descargado!");
    } catch { toast.error("Error generando el QR"); }
  }, [url, fgColor, bgColor, qColor, label, logoDataUrl]);

  // ── Guardar en localStorage ──────────────────────────────────
  const handleSave = () => {
    if (!url) return;
    setSaving(true);
    const entry = {
      id:          Date.now(),
      url,
      label:       label || url,
      fgColor,
      bgColor,
      qColor,
      logoDataUrl: logoDataUrl ?? null,
      size:        previewSize,
      createdAt:   new Date().toISOString(),
    };
    const updated = [entry, ...saved];
    setSaved(updated);
    localStorage.setItem("qk_qr_saved", JSON.stringify(updated));
    toast.success("QR guardado en la lista");
    setTimeout(() => setSaving(false), 600);
  };

  const handleDelete = (id) => {
    const updated = saved.filter(q => q.id !== id);
    setSaved(updated);
    localStorage.setItem("qk_qr_saved", JSON.stringify(updated));
  };

  const handleLoad = (q) => {
    setUrl(q.url);
    setLabel(q.label !== q.url ? q.label : "");
    setFgColor(q.fgColor);
    setBgColor(q.bgColor);
    setQColor(q.qColor);
    setLogoDataUrl(q.logoDataUrl ?? null);
    setPreviewSize(q.size || 300);
    toast.info("Configuración cargada en el editor");
  };

  const copyUrl = async () => {
    if (!url) return;
    await navigator.clipboard.writeText(url).catch(() => {});
    setCopying(true);
    setTimeout(() => setCopying(false), 2000);
  };

  const applyPreset = (p) => { setFgColor(p.fgColor); setBgColor(p.bgColor); setQColor(p.qColor); };
  const resetToDefault = () => { setFgColor("#1a1a2e"); setBgColor("#ffffff"); setQColor("#f59e0b"); };

  const previewQRSize = Math.min(previewSize, 280);

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 space-y-6">

      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-supernova-100 flex items-center justify-center shrink-0">
          <QrCodeIcon size={20} className="text-supernova-600" />
        </div>
        <div>
          <h1 className="text-xl font-black text-gray-800">Generador de Códigos QR</h1>
          <p className="text-xs text-gray-400">Con logo personalizado o la Q de Qkiere en el centro</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* ── Panel configuración ─────────────────────────────── */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 space-y-4">

          {/* URL */}
          <div>
            <label className="text-xs font-semibold text-gray-500 mb-1.5 block">
              URL o texto a codificar *
            </label>
            <div className="relative">
              <input
                value={url}
                onChange={e => setUrl(e.target.value)}
                placeholder="https://qkiere.com/company/123"
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-supernova-400 pr-9"
              />
              <button
                onClick={copyUrl}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-supernova-500 transition"
                title="Copiar URL"
              >
                {copying ? <Check size={14} className="text-green-500" /> : <Copy size={14} />}
              </button>
            </div>
          </div>

          {/* Etiqueta */}
          <div>
            <label className="text-xs font-semibold text-gray-500 mb-1.5 block">
              Etiqueta (para identificar)
            </label>
            <input
              value={label}
              onChange={e => setLabel(e.target.value)}
              placeholder="Ej: Menú restaurante, Perfil tienda…"
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-supernova-400"
            />
          </div>

          {/* Tamaño */}
          <div>
            <label className="text-xs font-semibold text-gray-500 mb-2 block">Tamaño del QR</label>
            <div className="flex gap-2">
              {SIZE_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setPreviewSize(opt.value)}
                  className={`flex-1 py-2 rounded-xl text-xs font-semibold border transition ${
                    previewSize === opt.value
                      ? "bg-supernova-500 text-white border-supernova-500"
                      : "bg-white text-gray-600 border-gray-200 hover:border-supernova-300"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Logo central */}
          <div>
            <label className="text-xs font-semibold text-gray-500 mb-2 flex items-center gap-1.5">
              <ImagePlus size={12} /> Logo central
            </label>

            {logoDataUrl ? (
              /* Logo cargado */
              <div className="flex items-center gap-3 p-3 rounded-xl border border-indigo-100 bg-indigo-50">
                <div
                  className="w-12 h-12 rounded-xl border border-gray-200 bg-white overflow-hidden shrink-0 flex items-center justify-center"
                >
                  <img src={logoDataUrl} alt="logo" className="w-full h-full object-contain" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-gray-700">Logo personalizado</p>
                  <p className="text-[10px] text-gray-400 mt-0.5">Se usará en el centro del QR</p>
                  <button
                    onClick={() => setLogoDataUrl(null)}
                    className="mt-1.5 text-[10px] text-red-400 hover:text-red-500 flex items-center gap-1 transition"
                  >
                    <X size={10} /> Quitar logo
                  </button>
                </div>
                <button
                  onClick={() => logoInputRef.current?.click()}
                  className="shrink-0 text-[10px] text-indigo-500 hover:text-indigo-700 flex items-center gap-1 transition font-semibold"
                  title="Cambiar imagen"
                >
                  <RefreshCw size={10} /> Cambiar
                </button>
              </div>
            ) : (
              /* Sin logo — por defecto usa Q */
              <div className="rounded-xl border border-dashed border-gray-200 p-3 flex items-center gap-3">
                <div
                  className="w-12 h-12 rounded-xl shrink-0 flex items-center justify-center font-black text-xl"
                  style={{ background: bgColor, color: qColor, boxShadow: `0 0 0 3px ${bgColor}, 0 0 0 4px ${qColor}20` }}
                >
                  Q
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-gray-600">Letra Q de Qkiere</p>
                  <p className="text-[10px] text-gray-400 mt-0.5">O sube tu propio logo</p>
                </div>
                <button
                  onClick={() => logoInputRef.current?.click()}
                  className="shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-xl bg-indigo-50 text-indigo-600 text-xs font-bold hover:bg-indigo-100 transition"
                >
                  <Upload size={13} /> Subir imagen
                </button>
              </div>
            )}

            <input
              ref={logoInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleLogoChange}
            />
          </div>

          {/* Colores */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-semibold text-gray-500 flex items-center gap-1.5">
                <Palette size={12} /> Colores
              </label>
              <button
                onClick={resetToDefault}
                className="text-[10px] text-gray-400 hover:text-supernova-500 transition flex items-center gap-1"
              >
                <RefreshCw size={10} /> Restaurar
              </button>
            </div>
            <div className="grid grid-cols-3 gap-3">
              {[
                { id: "fg", label: "QR",     value: fgColor, setter: setFgColor },
                { id: "bg", label: "Fondo",  value: bgColor, setter: setBgColor },
                { id: "q",  label: "Logo Q", value: qColor,  setter: setQColor, dim: !!logoDataUrl },
              ].map(({ id, label: lbl, value, setter, dim }) => (
                <div key={id} className={`flex flex-col items-center gap-1.5 ${dim ? "opacity-40" : ""}`}>
                  <label className="text-[10px] text-gray-400 font-medium">{lbl}</label>
                  <input
                    type="color"
                    value={value}
                    onChange={e => setter(e.target.value)}
                    disabled={!!dim}
                    className="w-10 h-10 rounded-xl cursor-pointer border border-gray-200 p-0.5 disabled:cursor-not-allowed"
                    style={{ background: value }}
                  />
                  <span className="text-[9px] font-mono text-gray-400">{value}</span>
                </div>
              ))}
            </div>
            {logoDataUrl && (
              <p className="text-[10px] text-gray-400 mt-1.5 text-center">
                El color "Logo Q" no aplica cuando usas imagen personalizada
              </p>
            )}
          </div>

          {/* Presets */}
          <div>
            <label className="text-xs font-semibold text-gray-500 mb-2 block">Temas predefinidos</label>
            <div className="flex flex-wrap gap-1.5">
              {PRESETS.map(p => (
                <button
                  key={p.name}
                  onClick={() => applyPreset(p)}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-gray-200 text-[11px] font-semibold text-gray-600 hover:border-supernova-300 hover:bg-supernova-50 transition"
                >
                  <span className="w-3 h-3 rounded-full border border-gray-300 shrink-0" style={{ background: p.qColor }} />
                  {p.name}
                </button>
              ))}
            </div>
          </div>

          {/* Botones acción */}
          <div className="flex gap-2 pt-1">
            <button
              onClick={handleDownload}
              disabled={!url}
              className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-supernova-500 text-white text-sm font-bold hover:bg-supernova-600 active:scale-95 transition disabled:opacity-40"
            >
              <Download size={16} /> Descargar PNG
            </button>
            <button
              onClick={handleSave}
              disabled={!url || saving}
              className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-gray-100 text-gray-700 text-sm font-bold hover:bg-gray-200 active:scale-95 transition disabled:opacity-40"
            >
              {saving ? <Check size={16} className="text-green-500" /> : <Plus size={16} />}
              Guardar
            </button>
          </div>
        </div>

        {/* ── Preview ────────────────────────────────────────── */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex flex-col items-center gap-4">
          <p className="text-xs font-bold text-gray-500 self-start flex items-center gap-1.5">
            <QrCodeIcon size={13} /> Vista previa en tiempo real
          </p>

          {url ? (
            <div
              ref={qrRef}
              className="relative flex items-center justify-center rounded-2xl shadow-inner"
              style={{ background: bgColor, padding: 16 }}
            >
              <QRCode
                value={url}
                size={previewQRSize}
                bgColor={bgColor}
                fgColor={fgColor}
                level="H"
              />
              <QCenter
                bgColor={bgColor}
                qColor={qColor}
                size={Math.round(previewQRSize * 0.155)}
                logoDataUrl={logoDataUrl}
              />
            </div>
          ) : (
            <div className="w-64 h-64 flex flex-col items-center justify-center bg-gray-50 rounded-2xl text-gray-300 gap-3">
              <QrCodeIcon size={48} />
              <p className="text-xs">Ingresa una URL para ver el preview</p>
            </div>
          )}

          {url && (
            <div className="text-center space-y-1">
              <p className="text-xs font-semibold text-gray-700 truncate max-w-xs">
                {label || "Sin etiqueta"}
              </p>
              <p className="text-[10px] text-gray-400 truncate max-w-xs">{url}</p>
            </div>
          )}

          <p className="text-[10px] text-gray-400 text-center max-w-[240px] leading-relaxed">
            El PNG de descarga es de alta resolución (800×800px) listo para imprimir.
          </p>
        </div>
      </div>

      {/* ── QRs guardados ──────────────────────────────────────── */}
      {saved.length > 0 && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm font-bold text-gray-700 flex items-center gap-2">
              <QrCodeIcon size={15} className="text-supernova-500" />
              QRs guardados
              <span className="bg-supernova-100 text-supernova-700 text-xs font-bold px-2 py-0.5 rounded-full">
                {saved.length}
              </span>
            </p>
            <button
              onClick={() => {
                if (!window.confirm("¿Eliminar todos los QRs guardados?")) return;
                setSaved([]);
                localStorage.removeItem("qk_qr_saved");
              }}
              className="text-[11px] text-red-400 hover:text-red-500 flex items-center gap-1 transition"
            >
              <X size={11} /> Limpiar todo
            </button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 gap-4">
            {saved.map(q => (
              <SavedQRCard
                key={q.id}
                q={q}
                onDelete={() => handleDelete(q.id)}
                onLoad={() => handleLoad(q)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
